import Config from '@apricityhealth/web-common-lib/Config';
import Axios from 'axios';
import React from 'react';

import {
    AppBar,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    IconButton,
    MenuItem,
    Paper,
    Tab, Tabs,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
    Select,
    FormControl,
    InputLabel
} from '@material-ui/core/';

import CodeIcon from '@material-ui/icons/Code';
import DeleteIcon from '@material-ui/icons/Delete';
import ExportIcon from '@material-ui/icons/CloudDownload';
import ImportIcon from '@material-ui/icons/CloudUpload';
import MergeIcon from '@material-ui/icons/MergeTypeOutlined';
import NavigationClose from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import ViewIcon from '@material-ui/icons/Visibility';

import Logger from '@apricityhealth/web-common-lib/utils/Logger';
import getErrorMessage from '@apricityhealth/web-common-lib/utils/getErrorMessage';
import SelectLanguage from '@apricityhealth/web-common-lib/components/SelectLanguage';
import SelectPlan from '@apricityhealth/web-common-lib/components/SelectPlan';
import SelectProvider from '@apricityhealth/web-common-lib/components/SelectProvider';
import SelectUser from '@apricityhealth/web-common-lib/components/SelectUser';
import UploadImage from "@apricityhealth/web-common-lib/components/UploadImage";
import SelectOrg from '@apricityhealth/web-common-lib/components/SelectOrg';
import User from '@apricityhealth/web-common-lib/components/User';
import EnhancedTable from "@apricityhealth/web-common-lib/components/EnhancedTable";
import OwnerInfo from '@apricityhealth/web-common-lib/components/OwnerInfo';
import SelectTimezone from '@apricityhealth/web-common-lib/components/SelectTimezone';

import {
    //eslint-disable-next-line
    Organization as Org,
    isArrayValid, 
    saveOrg
} from '@apricityhealth/web-common-lib/utils/Services';

import { toBoolean } from '@apricityhealth/web-common-lib/utils/Utils'
import { ImportExport } from '@apricityhealth/web-common-lib/components/ImportExport';

import AddressView from '../AddressView';
import ContactsView from './ContactsView';
import JSONDataDialog from '../../dialogs/JSONDataDialog';
import OfficeHoursView from './OfficeHoursView';
import OrgTrialsView from './OrgTrialsView';
import RolesView from './RolesView';
import RosterView from './RosterView';
import CodeView from './CodeView';

import '../../styles/org.css';
import ProvidersView from './ProvidersView';
import SelectSubscriptionPlans from '../../components/SelectSubscriptionPlan';
import { AdditionalTerms } from './AdditionalTerms';
import { AppointmentTemplates } from './AppointmentTemplates';
import { CompletionConfig } from './CompletionConfig';
import { PreVisitActionsConfig } from './PreVisitActionsConfig';
import { getTimePicker } from './EditOfficeHourDialog';
import Moment from 'moment-timezone';
import { TextOverrides } from './TextOverrides';

const LANGUAGES = require('@apricityhealth/web-common-lib/config/languages.json');

/**Accepted `org.orgType`'(s): ___DirectMessage-ACL is directly correlated___*/
const ORG_TYPES = Object.freeze(
[
    <MenuItem button key='agency'   value='agency'>Agency - Regulatory</MenuItem>,
    <MenuItem button key='employer' value='employer'>Employer</MenuItem>,
    <MenuItem button key='research' value='research'>Research - Clinical</MenuItem>,
    <MenuItem button key='practice' value='practice'>Practice - Clinical</MenuItem>,
    <MenuItem button key='unknown'  value='unknown'>Unknown - General OP</MenuItem>
]);

const ALERT_POLICY = Object.freeze([
    'permitted',
    'notPermitted'
]);

// Categories assigned to each org-config key (see `group:` on entries in
// ORG_CONFIG_KEYS). Drives the category filter dropdown on the Config
// Variables sub-tab. Keep group ids stable.
const CONFIG_GROUPS = Object.freeze([
    { id: 'branding_support',  label: 'Branding & Support' },
    { id: 'registration',      label: 'Registration & Baseline' },
    { id: 'notifications',     label: 'Notifications' },
    { id: 'legal',             label: 'Legal' },
    { id: 'patient_features',  label: 'Patient Features' },
    { id: 'alerts',            label: 'Alerts' },
    { id: 'advanced',          label: 'Advanced' },
]);

const ORG_CONFIG_KEYS = Object.freeze(
    [
        // Patient Features — UI toggles in the patient client
        { key: 'disableAppointments',          group: 'patient_features', type: 'boolean', desc: 'Disable Appointment button in the patient client.' },
        { key: 'disableCall',                  group: 'patient_features', type: 'boolean', desc: 'Disable the Call triage line button in the navigation drawer.' },
        { key: 'disableCheckin',               group: 'patient_features', type: 'boolean', desc: 'Disable the symptom check-in card and flow in the patient client.' },
        { key: 'disableCheckInReminder',       group: 'patient_features', type: 'boolean', desc: 'Disable check-in SMS reminder snackbar message.' },
        { key: 'disableCheckinReminderToggle', group: 'patient_features', type: 'boolean', desc: 'Hide the check-in reminder toggle in the patient Profile screen.' },
        { key: 'disableDailyCheck',            group: 'patient_features', type: 'boolean', desc: 'Disable daily check-in prompt.' },
        { key: 'disableDelegates',             group: 'patient_features', type: 'boolean', desc: 'Disable delegates feature of the patient client.' },
        { key: 'disableEducation',             group: 'patient_features', type: 'boolean', desc: 'Disable the education page in the patient client.' },
        { key: 'disableFax',                   group: 'patient_features', type: 'boolean', desc: 'Disable the Fax Report button in the eReport view.' },
        { key: 'disableHomeStatus',            group: 'patient_features', type: 'boolean', desc: 'Hide the weekly check-in ring chart and status band on the home screen.' },
        { key: 'disableMessages',              group: 'patient_features', type: 'boolean', desc: 'Disable Messaging button in the patient client.' },
        { key: 'disableReport',                group: 'patient_features', type: 'boolean', desc: 'Disable EReport button in the patient client.' },
        { key: 'disableRequestAppointment',    group: 'patient_features', type: 'boolean', desc: 'Hide the Request Appointment button in the Appointments view.' },
        { key: 'disableResources',             group: 'patient_features', type: 'boolean', desc: 'Disable the resources page in the patient client.' },
        { key: 'disableStatusPieChart',        group: 'patient_features', type: 'boolean', desc: 'Hide the status pie chart from the home screen carousel. If no education content is available either, the entire home status card is hidden.' },
        { key: 'disableSubscriptions',         group: 'patient_features', type: 'boolean', desc: 'Disable subscriptions feature of the patient client.' },
        { key: 'disableSurveys',               group: 'patient_features', type: 'boolean', desc: 'Hide the Surveys card on the home screen.' },
        { key: 'disableTrials',                group: 'patient_features', type: 'boolean', desc: 'Disable Trials button in the patient client.' },
        { key: 'enableAskApricity',           group: 'patient_features', type: 'boolean', desc: 'Enable the "Ask Apricity" AI assistant in the patient client. OFF by default: patients see no icon at all unless this is set, and the panel is absent rather than disabled. The assistant can answer questions about the patient it is opened for (and, for a delegate, anyone they act for) using their own check-ins, readings and alerts - it cannot reach any other patient. Server-side access checks apply per request regardless of this flag.' },
        { key: 'askApricityModel',            group: 'patient_features', type: 'string',  desc: 'Bedrock model ID the "Ask Apricity" assistant uses for this org’s patients, e.g. us.anthropic.claude-sonnet-5. Leave blank for the platform default. Patients cannot change it — the options gear is hidden in the patient client — so this is the only way to set it for them. An unrecognised ID will fail every request, so change it only to an ID listed by the admin AI model list.' },
        { key: 'enableConsolidatedHealth',     group: 'patient_features', type: 'boolean', desc: 'Enable the Consolidated Health card on the patient dashboard. Lets the patient connect their external EHR records via OAuth.' },
        { key: 'enableEmLife',                 group: 'patient_features', type: 'boolean', desc: 'Enable EmLife.' },
        { key: 'enableHomeVisitButton',        group: 'patient_features', type: 'boolean', desc: 'Show the "Next Home Visit" card on the patient dashboard. Pulls homeVisit-category appointments and surfaces the next upcoming visit with date, days-away, and Visit N chip. Hidden when the patient has no homeVisit appointments scheduled.' },
        { key: 'enableSaturn',                 group: 'patient_features', type: 'boolean', desc: 'Enable the SATURN cognitive assessment test in the patient client.' },
        { key: 'enableSurvey',                 group: 'patient_features', type: 'boolean', desc: 'Enable Survey', appendKey: true },
        { key: 'forcedCheckInReminder',        group: 'patient_features', type: 'boolean', desc: 'If true, then check-in SMS reminder will turn on automatically for new patients.' },
        { key: 'videoChat',                    group: 'patient_features', type: 'boolean', desc: 'Enable VideoChat button.' },

        // Registration & Baseline
        { key: 'baselineCheckDelay',           group: 'registration', type: 'number',  desc: 'How many days after accepting terms to ask for a baseline. (default 0)' },
        { key: 'disableBaselineCheck',         group: 'registration', type: 'boolean', desc: 'Disable baseline prompt.' },
        { key: 'disableRegister',              group: 'registration', type: 'boolean', desc: 'Disable user registration.' },
        { key: 'disableRegisterGender',        group: 'registration', type: 'boolean', desc: 'Disable asking for gender on registration.' },
        { key: 'disableRegisterRace',          group: 'registration', type: 'boolean', desc: 'Disable asking for race and ethnicity on registration.' },
        { key: 'disableOnboardingProvider',    group: 'registration', type: 'boolean', desc: 'Hide the "Oncologist" step of the provider portal onboarding wizard. Set true for non-clinical orgs that don\'t need to capture a referring oncologist; the wizard skips straight from Patient Information to Completion. Defaults to false (step visible).' },
        { key: 'externalIDP',                  group: 'registration', type: 'boolean', desc: 'Using external identity provider (IDP).' },
        { key: 'onboardingPreScreenModels',    group: 'registration', type: 'string',  desc: 'CSV of Rosetta detect-model IDs that drive the PreScreen step of the provider portal onboarding wizard. When set, an extra "PreScreen" step appears between New Patient and Patient Information; the listed models run inside a ConversationView and the participant only advances on a "pass" flag. Leave blank to skip PreScreen (default).' },
        { key: 'prospectivePatientCount',      group: 'registration', type: 'string',  desc: 'The number of prospective patients for this org. Used for reporting.' },

        // Alerts — policies, clearance, EOD messages, reminder alerts
        { key: 'confirmed_covidAlert_clearance', group: 'alerts', type: 'boolean',  desc: 'Require clearance for confirmed covidAlert.' },
        { key: 'confirmed_covidAlert_policy',    group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for confirmed covidAlert.' },
        { key: 'confirmedEODMessage',            group: 'alerts', type: 'string',   desc: 'Message displayed to the user on a confirmed alert level.', appendLanguage: true },
        { key: 'high_covidAlert_clearance',      group: 'alerts', type: 'boolean',  desc: 'Require clearance for high covidAlert.' },
        { key: 'high_covidAlert_policy',         group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for high alerts.' },
        { key: 'high_exposureAlert_clearance',   group: 'alerts', type: 'boolean',  desc: 'Require clearance for high exposureAlert.' },
        { key: 'high_exposureAlert_policy',      group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for high alerts.' },
        { key: 'highEODMessage_',                group: 'alerts', type: 'string',   desc: 'Message displayed to the user on a high alert level.', appendLanguage: true },
        { key: 'low_covidAlert_clearance',       group: 'alerts', type: 'boolean',  desc: 'Require clearance for low covidAlert.' },
        { key: 'low_covidAlert_policy',          group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for low covidAlert.' },
        { key: 'low_exposureAlert_clearance',    group: 'alerts', type: 'boolean',  desc: 'Require clearance for low exposureAlert.' },
        { key: 'low_exposureAlert_policy',       group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for low exposureAlert.' },
        { key: 'lowEODMessage',                  group: 'alerts', type: 'string',   desc: 'Message displayed to the user on a low alert level.', appendLanguage: true },
        { key: 'medium_covidAlert_clearance',    group: 'alerts', type: 'boolean',  desc: 'Require clearance for medium covidAlert.' },
        { key: 'medium_covidAlert_policy',       group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for medium covidAlert.' },
        { key: 'medium_exposureAlert_clearance', group: 'alerts', type: 'boolean',  desc: 'Require clearance for medium exposureAlert.' },
        { key: 'medium_exposureAlert_policy',    group: 'alerts', type: 'selector', options: ALERT_POLICY, desc: 'Default policy for medium exposureAlert.' },
        { key: 'mediumEODMessage',               group: 'alerts', type: 'string',   desc: 'Message displayed to the user on a medium alert level.', appendLanguage: true },
        { key: 'quarantineEODMessage',           group: 'alerts', type: 'string',   desc: 'Message displayed to the user on a quarantine alert level.', appendLanguage: true },
        { key: 'reminderAlertMessage',           group: 'alerts', type: 'string',   desc: 'Reminder alert SMS override.', appendLanguage: true },

        // Notifications — invite + reminder email / SMS
        { key: 'invite_patient_emailMessage',    group: 'notifications', type: 'string', desc: 'Patient welcome email body.',                 appendLanguage: true },
        { key: 'invite_patient_emailSubject',    group: 'notifications', type: 'string', desc: 'Patient welcome email subject.',              appendLanguage: true },
        { key: 'invite_patient_smsMessage',      group: 'notifications', type: 'string', desc: 'Patient welcome SMS message.',                appendLanguage: true },
        { key: 'reminder_patient_emailMessage',  group: 'notifications', type: 'string', desc: 'Reminder email override body (patient).',     appendLanguage: true },
        { key: 'reminder_patient_emailSubject',  group: 'notifications', type: 'string', desc: 'Reminder email override subject (patient).',  appendLanguage: true },
        { key: 'reminder_patient_smsMessage',    group: 'notifications', type: 'string', desc: 'Reminder SMS override body (patient).',       appendLanguage: true },
        { key: 'reminder_provider_emailMessage', group: 'notifications', type: 'string', desc: 'Reminder email override body (provider).',    appendLanguage: true },
        { key: 'reminder_provider_emailSubject', group: 'notifications', type: 'string', desc: 'Reminder email override subject (provider).', appendLanguage: true },
        { key: 'reminder_provider_smsMessage',   group: 'notifications', type: 'string', desc: 'Reminder SMS override body (provider).',      appendLanguage: true },

        // Branding & Support — per-org appearance + content URL overrides + support contact
        { key: 'availableThemes',       group: 'branding_support', type: 'string',   desc: 'Comma-separated list of theme names this org\'s users can choose from in the patient client\'s Settings (e.g. "light,regenimo"). Order in the list is the display order. When unset, defaults to all universal themes (light, dark) plus any partner theme matching this org\'s themeOverride.' },
        { key: 'companyPolicyPage',     group: 'branding_support', type: 'string',   desc: 'Company policy page (HTML supported).', appendLanguage: true },
        { key: 'guidelinesURL',         group: 'branding_support', type: 'string',   desc: 'URL embedded in the Guidelines page in the provider client. When set it replaces the default guidelines page with an iframe of this URL.' },
        { key: 'logoBackgroundColor',   group: 'branding_support', type: 'string',   desc: 'Background color for the logo + user-identity area in the patient client nav drawer. Set to match the logo image background (e.g. "#FFFFFF" for a logo on a white background). Defaults to "transparent" which inherits the drawer color.' },
        { key: 'showPoweredBy',         group: 'branding_support', type: 'boolean',  desc: 'Show the "Powered by Apricity Health" footer in the patient client nav drawer. Defaults to true.' },
        { key: 'themeOverride',         group: 'branding_support', type: 'string',   desc: 'Override the theme for the patient client. Only applied on first launch; user selection in localStorage takes over after.' },

        // Legal — text overrides for legal/policy documents + the additional-terms JSON
        { key: 'addPatientTerms',         group: 'legal', type: 'string', desc: 'Additional terms to ask for in the patient client. Edited via the JSON editor below the flat keys.' },
        { key: 'noticeOfPrivacyPractice', group: 'legal', type: 'string', desc: 'Private Practice text override.', appendLanguage: true },
        { key: 'privacyNotice',           group: 'legal', type: 'string', desc: 'Privacy Policy text override.',   appendLanguage: true },
        { key: 'termsOfService',          group: 'legal', type: 'string', desc: 'Terms of service text override.', appendLanguage: true },

        // Advanced — JSON-formatted templates and overrides
        { key: 'appointmentMessageTemplates',  group: 'advanced', type: 'string', desc: 'Appointment message templates. (JSON format)' },
        { key: 'preVisitActions',              group: 'advanced', type: 'string', desc: 'Pre-visit action buttons shown on the patient dashboard under a "Before your visit" header. JSON shape: [{ icon?, title, description?, language?, models, dataId?, days?, color?, bgColor? }]. Each button gates on getPatientData: if data exists for dataId within `days` it is hidden, otherwise it appears and a click opens a check-in restricted to `models`. When all buttons are gated the patient sees an "all caught up" message. `color` sets the icon color, `bgColor` sets the button background. (JSON format)' },
        { key: 'onboardingCompletionConfig',   group: 'advanced', type: 'string', desc: 'Customizes the final "Completion" step of the provider portal onboarding wizard. JSON shape: { steps: [{ title, detail?, linkTitle?, linkUrl?, deferred? }] }. Each step shows a numbered badge; if linkUrl is set, a button labeled linkTitle (or the URL) opens the URL in a new browser window. When unset, the Completion step renders the legacy training-steps content. (JSON format)' },
        { key: 'textOverrides',                group: 'advanced', type: 'string', desc: 'Text overrides for this organization. (JSON format)' },
    ].sort((a,b) => a.key.localeCompare(b.key))
)

// Look up which group a given config key belongs to. Falls back to 'advanced'
// for unknown keys (legacy entries) so they remain visible in the editor.
function getConfigKeyGroup(key) {
    if (!key) return null;
    const def = ORG_CONFIG_KEYS.find((k) => {
        if (k.appendLanguage || k.appendKey) return key.startsWith(k.key);
        return k.key === key;
    });
    return (def && def.group) || 'advanced';
}
const log = new Logger();

function isValidDate(d) {
    let ts = Date.parse(d);
    if (isNaN(ts))
        return false;
    else
        return true;
}

/**
 * @extends React.Component
 * Apricity Tenant-Organization Viewer
 * @memberof module:Admin
 * @alias OrgView
 */
class OrgView extends React.Component {
//#region Component-Lifecycle Methods
    constructor (props) {
        super(props);
        this.state = {
            configArray: [],
            dialog: null,
            export_progress: null,
            currentTab: '0',
            currentConfigTab: '0',
            configCategoryFilter: 'all',
            tabs: [],
            configTabs: [],
            org: null,
            patients: [],
            providers: [],
            users: [],
            mergeOrgId: ''
        };

        if (typeof this.props.onClose !== 'function')
            log.warning('`OrgView.onClose()` event not found');
    }


    componentDidMount() {
        this.loadOrg(this.props.orgId);
    }

    componentDidUpdate(oldProps) {
        const { props: { orgId } } = this,
            { orgId: oldOrgId } = oldProps;

        if (orgId && typeof orgId !== 'string') throw new TypeError('`props.orgId` must be a string');
        if (orgId !== oldOrgId) this.setState({ orgId }, this.loadOrg.bind(this, orgId));
    }


    closeOrg() {
        this.props.onClose();
    }

    onCloseDialog() {
        this.setState({ dialog: null });
    }

    onMergeOrg() {
        const { org } = this.state;
        const { appContext } = this.props;

        this.setState({
            dialog: <Dialog open={true}>
                <DialogTitle>Merge Org:</DialogTitle>
                <DialogContent>
                    Please select an org to merge this org into?<br />
                    PLEASE NOTE: This is not reversable, SO MAKE SURE YOU WANT TO MERGE!
                    <SelectOrg appContext={appContext} onChange={(orgId) => {
                        console.log("selected org:", orgId );
                        this.setState({mergeOrgId: orgId });
                    }} />
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={this.onCloseDialog.bind(this)}>Cancel</Button>,
                    <Button variant='contained' onClick={() => {
                        this.setState({ dialog: null, progress: <CircularProgress size={20} />, error: null });
                        const request = {
                            url: `${Config.baseUrl + Config.pathPrefix}orgs/merge`,
                            method: 'POST',
                            headers: { "Authorization": appContext.state.idToken },
                            data: { sourceOrgId: org.orgId, targetOrgId: this.state.mergeOrgId }
                        };

                        log.debug('mergeOrg request:', request);
                        Axios(request).then((response) => {
                            log.debug('mergeOrg response:', response);
                            const org = response.data.sourceOrg;
                            this.transformOrg(org);
                            this.setState({ org, progress: null }, this.createTabs.bind(this));
                        }).catch((err) => {
                            log.error('mergeOrg error:', err);
                            this.setState({ progress: null, error: getErrorMessage(err) }, this.createTabs.bind(this));
                        });
                    }}>Confirm</Button>
            </DialogActions>
            </Dialog>
        })
    }

    onDeleteOrg() {
        const { state: { org } } = this;
        this.setState({
            dialog: <Dialog open model='false'>
                <DialogTitle>Delete Org: {org.orgId}</DialogTitle>
                <DialogContent>
                    <DialogContentText>Are you sure you want to delete?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={this.onCloseDialog.bind(this)}>Cancel</Button>,
                    <Button variant='contained' onClick={this.confirmDelete.bind(this)}>Confirm</Button>
                </DialogActions>
            </Dialog>
        });
    }

    /**@param {String} orgId Apricity Tenant-Organization Identifier*/
    loadOrg(orgId) {
        log.debug(`loadOrg ${orgId}`);
        const { props: { appContext } } = this;

        if (orgId && typeof orgId === 'string') {
            this.setState({ progress: <CircularProgress size={20} />, error: null });
            const request = {
                url: `${Config.baseUrl + Config.pathPrefix}orgs/${orgId}`,
                method: 'GET',
                headers: { "Authorization": appContext.state.idToken }
            };
            log.debug('getOrg request:', request);
            Axios(request).then((response) => {
                log.debug('getOrg response:', response);
                const { data: { org } } = response;
                this.transformOrg(org);
                this.setState({ org, progress: null }, this.createTabs.bind(this));
            }).catch((err) => {
                log.error('getOrg error:', err);
                this.setState({ progress: null, error: getErrorMessage(err) }, this.createTabs.bind(this));
            });

        } else {
            this.setState({
                org: {
                    orgId: '',
                    name: '',
                    officeHours: [],
                    contacts: [],
                    address: [],
                    trialIds: [],
                    config: {}
                }
            }, this.createTabs.bind(this));
        }
    }

    /**Save the currently-selected Org*/
    saveOrg() {
        const { props: { appContext }, state: { org } } = this;

        org.NPIs = org.NPIs.filter((e) => e);           // remove any blank NPI's

        this.untransformOrg(org);
        this.setState({ progress: <CircularProgress size={20} />, error: null });

        saveOrg(appContext, org).then( uOrg => {
            if (! org || ! uOrg || typeof uOrg.orgId !== 'string') return void (
                this.transformOrg(org),
                this.setState({
                    org,
                    progress: null,
                    error: `Failed to update Apricity Tenant-Organization<${uOrg.orgId}>`
                })
            );
            else return void (
                this.transformOrg(uOrg),
                this.setState({ org: uOrg, progress: null, error: null })
            );
        }).catch( error => void (
            this.transformOrg(org),
            this.setState({ org, progress: null, error: getErrorMessage(error) })
        ));
    }

    saveUser(account) {
        const { props: { appContext } } = this;
        return new Promise((resolve, reject) => {
            const saveUser = {
                url: `${Config.baseUrl + Config.pathPrefix}authentication/users`,
                method: 'POST',
                headers: { "Authorization": appContext.state.idToken },
                data: account
            };
            // log.debug('saveUser:', saveUser);
            Axios(saveUser).then(function (response) {
                // log.debug('saveUser response:', response);
                resolve(response.data.user);
            }).catch(function (error) {
                log.error('saveUser error:', error);
                reject(null);
            });
        });
    }

    /**@param {React.ChangeEvent} e `org.orgType` event trigger*/
    onOrgType(e) {
        const { state: { org } } = this,
            { target: { value: orgType } } = e;
        if (typeof orgType !== 'string') return void this.setState({
            error: 'onOrgType: `e.target.value: orgType` must be a string',
            progress: null
        });
        else if (org) return void (
            org.orgType = (orgType || 'unknown'),
            this.setState({ org, progress: null })
        );
        else return void this.setState({
            error: 'no Org selected',
            progress: null
        });
    }
    /**Display Apricity Tenant-Organization's ___rawJSON___*/
    onShowCode() {
        this.setState({
            dialog: <JSONDataDialog
                appContext={this.props.appContext}
                dataType={this.state.org}
                onDone={this.onCloseDialog.bind(this)} />
        });
    }
    /**@param {React.ChangeEvent} e `org.timeZone` event trigger*/
    onTimeZone(timeZone) {
        const { state: { org } } = this;
        if (typeof timeZone !== 'string') return void this.setState({
            error: 'onTimeZone: `e.target.value: timeZone` must be a string',
            progress: null
        });
        else if (! org) return void this.setState({
            error: 'no Org selected',
            progress: null
        });
        else return void (
            org.timeZone = (timeZone || Moment.tz.guess()),
            this.setState({ org, progess: null })
        );
    }
//#endregion


    /**
     * Modify/Update Org values
     * @param {Org} org Target Apricity Tenant-Organization
     */
    transformOrg(org) {
        const config = [];
        if (! org.config) org.config = {};
        for (const key in org.config) {
            config.push({ key, value: org.config[key], desc: (ORG_CONFIG_KEYS.find((e) => e.key === key) || { desc: ''}).desc });
        }
        org.configArray = config;
        this.setState({ configArray: config, org }, this.createTabs.bind(this));
    }

    /**
     * Un-Modify/Update Org values
     * @param {Org} org Target Apricity Tenant-Organization
     */
    untransformOrg(org, deleteConfigArray = true ) {
        const { configArray } = this.state;
        const config = {};
        for (let i = 0; i < configArray.length; ++i) {
            config[configArray[i].key] = configArray[i].value;
        }
        org.config = config;
        if ( deleteConfigArray ) {
            delete org.configArray;// keep this here to remove the configArray from org's we accidently added this data onto
            this.setState({ org }, this.createTabs.bind(this));
        } else {
            this.setState({ org }, this.createTabs.bind(this));
        }
    }

//#region Import|Export-Org|Patient Data
    /**Push `DELETE` for the currently selected Apricity Tenant-Organization*/
    confirmDelete() {
        const {
            props: { appContext },
            state: { org }
        } = this;
        this.setState({ dialog: null, error: null, progress: <CircularProgress size={20} /> });
        if (org.orgId) {
            const request = {
                url: `${Config.baseUrl + Config.pathPrefix}orgs/${org.orgId}`,
                method: 'DELETE',
                headers: { "Authorization": appContext.state.idToken }
            };
            log.debug('deleteOrg request:', request);
            Axios(request).then((/**@this {OrgView}*/function (response) {
                log.debug('deleteOrg response:', response);
                this.setState({ progress: null }, this.closeOrg.bind(this));
            }).bind(this)
            ).catch((/**@this {OrgView}*/function (error) {
                log.error('deleteOrg error:', error.response);
                this.setState({ progress: null, error: getErrorMessage(error) });
            }).bind(this));
        }
    }

    confirmImportPatients() {
        const {
            props: { appContext },
            state: { org: { orgId = '' } = {} }
        } = this;
        log.info(`org id: ${orgId}`);
        if (orgId === '') this.setState({
            export_progress: null, error: null,
            dialog: <Dialog open aria-labelledby="form-dialog-title">
                <DialogTitle>Warning</DialogTitle>
                <DialogContent>
                    <div>The org must be created and have a valid org id before patients can be imported.</div>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={this.onCloseDialog.bind(this)}>Ok</Button>,
                </DialogActions>
            </Dialog>
        });
        else this.setState({
            dialog: <PatientsImport appContext={appContext} cancel={this.onCloseDialog.bind(this)}
                ok={this.importPatients.bind(this)} />
        });
    }

    importPatients(data) {
        const {
            props: { appContext },
            state: { org: { orgId } }
        } = this,
            { bucket, firstName, lastName, newPatientId, providerId, userId } = data;
        const args = [];

        this.setState({ dialog: null, error: null, export_progress: <CircularProgress size={20} /> });

        if (firstName) args.push(`firstName=${firstName}`);
        if (lastName) args.push(`lastName=${lastName}`);
        if (bucket) args.push(`fileId=${bucket}`);
        if (orgId) args.push(`orgId=${orgId}`);
        if (providerId) args.push(`providerId=${providerId}`);
        if (userId) args.push(`userId=${userId}`);
        if (String(newPatientId) === 'true') args.push(`newPatientId=true`);

        const request = {
            url: `${Config.baseUrl + Config.pathPrefix}apricity-cleanse/patients/?${args.join('&')}`,
            method: 'post',
            headers: { "Authorization": appContext.state.idToken }
        };
        log.debug('importPatients request', request);
        Axios(request).then((response) => {
            log.debug('importPatients response:', response);
            this.setState({ export_progress: null, error: null });
        }).catch((error) => {
            log.error('importPatients request', error);
            this.setState({ export_progress: null, error: getErrorMessage(error) });
        });
    }

    exportPatients() {
        this.setState({
            dialog: <Dialog open aria-labelledby="form-dialog-title">
                <DialogTitle>Exporting Org Patients</DialogTitle>
                <DialogContent>
                    <div>This may take longer than 30 seconds.</div>
                    <div> Once you select ok, you will see a dialog with the link and file id to use for the import.</div>
                </DialogContent>
                <DialogActions>
                    <Button variant='contained' onClick={this.performOrgExport.bind(this)}>Ok</Button>
                    <Button variant='contained' onClick={this.onCloseDialog.bind(this)}>Cancel</Button>
                </DialogActions>
            </Dialog>
        });
    }

    performOrgExport() {
        const {
            props: { appContext },
            state: { org }
        } = this,
            orgId = org.orgId;
        const s3URL = `https://s3.console.aws.amazon.com/s3/buckets/patientexport.develop.apricity-health?region=us-east-1&tab=objects`;

        this.setState({ export_progress: <CircularProgress size={20} />, error: null });

        const request = {
            url: `${Config.baseUrl + Config.pathPrefix}apricity-cleanse/patients/?orgId=${orgId}`,
            method: 'GET',
            headers: { "Authorization": appContext.state.idToken }
        };
        log.debug('exportPatient request', request);
        Axios(request).then((response) => {
            log.debug('exportPatient response:', response);
            const data = response.data;
            const s3DirectURL = `https://s3.console.aws.amazon.com/s3/buckets/patientexport.develop.apricity-health?region=us-east-1&prefix=${data}/&showversions=false`;

            this.setState({
                export_progress: null, error: null,
                dialog: <Dialog open aria-labelledby="form-dialog-title">
                    <DialogTitle>Exporting Org Patients</DialogTitle>
                    <DialogContent>
                        <div>The org is exporting, the file id is: {data}.</div>
                        <br />
                        <div>The exported orgs are here: <a href={s3URL}>Export Buckets</a>.</div>
                        <br />
                        <div>Direct link here: <a href={s3DirectURL}>{data}</a>. Note: this link won't exist until the export is complete. Refresh until the link exists.</div>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='contained' onClick={this.onCloseDialog.bind(this)}>Ok</Button>,
                    </DialogActions>
                </Dialog>
            });
        }).catch((error) => {
            this.setState({ export_progress: null, error: getErrorMessage(error) });
        });
    }
//#endregion

//#region Organizational-Data Fetching
    exportActivationCodes(limit) {
        log.debug(`exportActivationCodes limit: ${limit}`);
        this.setState({ progress: <CircularProgress /> });
        return new Promise((resolve, reject) => {
            let report = [];
            this.getNextActivationCodes(limit).then( codes => {
                if (isArrayValid(codes)) {
                    for (let i = 0; i < codes.length; i++) report.push({
                        index: i, activationCode: (codes[i] && codes[i].activationCode)
                    });
                }

                this.setState({ progress: null });
                resolve(report);
            }).catch( error => {
                log.error('exportActivationCodes error:', error);
                this.setState({ progress: error });
                reject(error);
            });
        });
    }

    getNextActivationCodes(count = 10) {
        const { props: { appContext, orgId } } = this;
        return new Promise((resolve, reject) => {
            if (typeof count !== 'number' || count === 0 || isNaN(count)) count = 10;
            if (orgId) {
                const request = {
                    url: `${Config.baseUrl + Config.pathPrefix}orgs/${orgId}/codes/${count}`,
                    method: 'GET',
                    headers: { "Authorization": appContext.state.idToken }
                };
                console.log('getNextActivationCodes request', request);
                Axios(request).then((response) => {
                    console.log('getNextActivationCodes response:', response.data);
                    resolve(response.data);
                }).catch((err) => {
                    console.error('getNextActivationCodes error:', err.response.data.error);
                    reject(err.response.data.error);
                });
            } else {
                reject(new Error('No org id defined'));
            }
        });
    }


    getAccount(appContext, userId) {
        return new Promise((resolve, reject) => {
            const request = {
                url: `${Config.baseUrl + Config.pathPrefix}authentication/users/${userId}`,
                method: 'GET',
                headers: { "Authorization": appContext.state.idToken }
            };
            log.debug('getAccount request:', request);
            Axios(request).then(function (response) {
                log.debug('getAccount response:', response);
                resolve(response.data.user);
            }).catch(function (error) {
                log.error('getAccount error:', getErrorMessage(error));
                reject(error);
            });
        });
    }
//#endregion


    createTabs() {
        const {
            props: { appContext },
            state: { configArray, org, currentConfigTab, configCategoryFilter }
        } = this;

        if (! org) return void this.setState({ error: 'no Org selected', progress: null, tabs: [] });
        if (! org.directMessageDomain ) org.directMessageDomain = '';
        if (! org.overnightHours ) org.overnightHours = { start: 1380, end: 420 };
         
        if (! Array.isArray(org.planIds)) org.planIds = [];
        if (! Array.isArray(org.trialIds)) org.trialIds = [];

        const tabs = [];
        const configTabs = [];
        const locationColumns = [
            { id: 'locationId', label: 'Location Id'                                 },
            { id: 'name',       label: 'Location Name', editType: 'text', width: 250 },
            { id: 'default',    label: 'Default',    editControl: (table, value, row, index, id) =>
                <FormControlLabel label='Default' onClick={table.preventClickThrough.bind(table)}
                    control={<Checkbox checked={(String(value) === 'true')} onChange={(e, v) => {
                        table.setDataField(index, id, v);
                        for (let i = 0; i < table.state.data.length; ++i)
                            if (i !== index && table.state.data[i][id]) table.setDataField(i, id, false);
                    }} />}
                />
            }
        ],
        planColumns = [
            { id: 'planId',  label: 'Plan', numeric: false, disabledPadding: false,
                editControl: (table, value, row, index, id) => <SelectPlan appContext={appContext} enableNone={true}
                    style={{ margin: 5, width: 300 }} planId={value} onChange={(plan) => {
                        if (plan && plan.planId !== value) table.setDataField(index, id, plan.planId);
                    }}
                />
            },
            { id: 'default', label: 'Default', editControl: (table, value, row, index, id) =>
                <FormControlLabel label='Default' onClick={table.preventClickThrough.bind(table)}
                    control={<Checkbox checked={String(value) === 'true'} onChange={(e, v) => {
                        //table.preventClickThrough(e);
                        table.setDataField(index, id, v);
                        for (let i = 0; i < table.state.data.length; ++i)
                            if (i !== index && table.state.data[i][id]) table.setDataField(i, id, false);
                    }} />}
                />
            }
        ],
        configColumns = [
            { id: '_index', label: 'Order', numeric: true },
            { id: 'key', label: 'Key', editControl: (table, value, row, index, id) => {

                // When the row was added under a category filter (carries a
                // transient _group) and hasn't been keyed yet, restrict the
                // picker to that category's keys.
                const restrictToGroup = (!row.key && row._group) ? row._group : null;
                const availableKeys = restrictToGroup
                    ? ORG_CONFIG_KEYS.filter((k) => k.group === restrictToGroup)
                    : ORG_CONFIG_KEYS;

                const getSelect = (value, append = '') => <Select value={value} onChange={(e) => {
                    const selected = e.target.value + append;
                    table.setDataField(index, id, selected );
                    table.setDataField(index, 'desc', (ORG_CONFIG_KEYS.find((e) => e.key === selected) || { desc: ''}).desc )
                }} style={{ width: 400, marginLeft: 5 }}>
                    <MenuItem value={'_'}>Custom Value</MenuItem>
                    {availableKeys.map((i) => <MenuItem key={i.key} id={i.key} value={i.key}>{i.key}</MenuItem>)}
                </Select>;

                const appendLangage = ORG_CONFIG_KEYS.findIndex((k) => k.appendLanguage && value.startsWith(k.key));
                if ( appendLangage >= 0 ) {
                    const config = ORG_CONFIG_KEYS[appendLangage];
                    const language = value.substring(config.key.length + 1);            // skip the underscore, thus the +1
                    if (! language ) {
                        // if no language was provided, default to en-us
                        table.setDataField(index, id, config.key + '_en-us');
                    }

                    return <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {getSelect(config.key)}
                        <Select value={language} onChange={(e) => table.setDataField(index, id, config.key + "_" + e.target.value)} style={{ width: 200, marginLeft: 5}}>
                            {Object.keys(LANGUAGES).map((k) => <MenuItem value={k}>{LANGUAGES[k]}</MenuItem>)}
                        </Select>
                    </div>;
                }

                // check for appendKey type of config, where the first part is a given key, then a unique ID following...
                const appendKeyIndex = ORG_CONFIG_KEYS.findIndex((k) => k.appendKey && value.startsWith(k.key));
                if ( appendKeyIndex >= 0 ) {
                    const config = ORG_CONFIG_KEYS[appendKeyIndex];
                    const appendKey = value.substring(config.key.length);

                    return <div style={{ display: 'flex', flexDirection: 'row' }}>
                        {getSelect(config.key)}
                        <TextField value={appendKey} onChange={(e) => table.setDataField(index, id, config.key + e.target.value)} style={{marginLeft: 5, width: 200}} placeholder='ID' />
                    </div>
                }

                if (ORG_CONFIG_KEYS.find((k) => k.key === value) || !value) {
                    return getSelect(value);
                }

                // lastly, if we don't find the key in the ORG_CONFIG_KEYS, assume this is a custom control then..
                return <TextField value={value} onChange={(e) => table.setDataField(index, id, e.target.value)} style={{marginLeft: 5, width: 400}} />;
            } },  
            { id: 'value', label: 'Value', editControl: (table, value, row, index, id) => {
                let config = ORG_CONFIG_KEYS.find((c) => {
                    if ( c.appendLanguage ) return row.key.startsWith(c.key);
                    if ( c.appendKey ) return row.key.startsWith(c.key);

                    return c.key === row.key;
                })

                if ( config ) {
                    switch(config.type) {
                        case 'boolean':
                            return <Select value={value} onChange={(e) => table.setDataField(index, id, e.target.value)} style={{width: 100}}>
                                <MenuItem value={'true'}>True</MenuItem>
                                <MenuItem value={'false'}>False</MenuItem>
                            </Select>; 
                        case 'selector':
                            return <Select value={value} onChange={(e) => table.setDataField(index, id, e.target.value)} style={{width: 200}}>
                                {config.options.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                            </Select>;
                        case 'number':
                            return <TextField style={{width: 200}} key={row.key} value={value} type='number'
                                onChange={(e) => table.setDataField(index, id, e.target.value)} />;
                        default:
                            break;
                    }    
                }

                return <TextField style={{width: 500}} key={row.key} value={value} 
                    onChange={(e) => table.setDataField(index, id, e.target.value)} />;
            } },
            { id: 'desc', label: 'Description' }
        ],
        orgFieldsColumns = [
            { id: 'id', label: 'Field ID', editType: 'text' },
            { id: 'description', label: 'Description', editType: 'text', width: 400 },
            { id: 'required', label: 'Required', editType: 'checkbox' },
            { id: 'minLength', label: 'Min Length', editType: 'number' },
            { id: 'maxLength', label: 'Max Length', editType: 'number' }
        ],
        alertFilterColumns = [
            { id: 'modelId', label: 'Model ID', editType: 'text' },
            { id: 'category', label: 'Category', editType: 'text' },
            { id: 'patientId', label: 'Patient ID', editType: 'text' }
        ];

        tabs.push(<Paper key='Providers'>
            <ProvidersView appContext={appContext} org={org} />
        </Paper>);
        tabs.push(<Paper key='Locations'>
            <EnhancedTable
                title='Locations'
                rowsPerPage={5}
                orderBy='planId'
                columnData={locationColumns}
                data={org.locations}
                onDataChanged={(data, table) => {
                    //make sure falses are false, prevent server errors
                    data.forEach( row => {
                        row.default = toBoolean(row.default);
                    });
                    org.locations = data;
                    this.setState({ org });
                }}
            />
        </Paper>);
        tabs.push(<OfficeHoursView key='Office Hours' orgId={org.orgId} officeHours={org.officeHours} timeZone={org.timeZone} appContext={appContext} />);
        if ( org.enableAppointments ) {
            tabs.push(<OfficeHoursView key='Appt Hours' orgId={org.orgId} officeHours={org.appointmentHours} timeZone={org.timeZone} appointments={true} appContext={appContext} />)
        }
        tabs.push(<AddressView key='Addresses' address={org.address} appContext={appContext} />);
        tabs.push(<ContactsView key='Contacts' org={org} appContext={appContext} />);
        tabs.push(<OrgTrialsView key='Trials' trialIds={org.trialIds} appContext={appContext} />);
        tabs.push(<Paper key='Plans'>
            <EnhancedTable
                title='Plans'
                rowsPerPage={5}
                orderBy='planId'
                columnData={planColumns}
                data={org.planIds}
                onDataChanged={(data, table) => { // make sure falses are false, prevent server errors
                    if (isArrayValid(data)) { 
                        for (const row of data) {
                            row.default = toBoolean(row.default);
                        }
                    }
                    org.planIds = data;
                    this.setState({ org });
                }}
            />
        </Paper>);// ONLY `research` Organization(s) have an Access Control List Tab

        tabs.push(<Paper key='Alert Filters'>
            <EnhancedTable
                title='Alert Filters'
                rowsPerPage={5}
                orderBy='_id'
                columnData={alertFilterColumns}
                data={org.alertFilters}
                onDataChanged={(data) => {
                    org.alertFilters = data;
                    this.setState({ org });
                }}
            />
        </Paper>);

        const rowMatchesFilter = (row) => {
            if (configCategoryFilter === 'all') return true;
            if (!row.key) return row._group === configCategoryFilter;
            return getConfigKeyGroup(row.key) === configCategoryFilter;
        };
        const filteredConfigRows = configArray.filter(rowMatchesFilter);

        const categoryButtons = [{ id: 'all', label: 'All' }, ...CONFIG_GROUPS];
        configTabs.push(<Paper key='Config Variables'>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: 10 }}>
                {categoryButtons.map((g) => {
                    const selected = configCategoryFilter === g.id;
                    return <Button key={g.id} size='small'
                        variant={selected ? 'contained' : 'outlined'}
                        color={selected ? 'primary' : 'default'}
                        style={{ textTransform: 'none' }}
                        onClick={() => this.setState({ configCategoryFilter: g.id }, this.createTabs.bind(this))}>
                        {g.label}
                    </Button>;
                })}
            </div>
            <EnhancedTable
                title='Config Variables'
                rowsPerPage={5}
                orderBy='key'
                columnData={configColumns}
                data={filteredConfigRows}
                onDataChanged={(data) => {
                    const other = configArray.filter((r) => !rowMatchesFilter(r));
                    this.setState({ configArray: [ ...other, ...data ] }, this.untransformOrg.bind(this, org, false));
                }}
                onAdd={() => {
                    const newRow = { key: '', value: '' };
                    if (configCategoryFilter !== 'all') newRow._group = configCategoryFilter;
                    this.setState({ configArray: [ newRow, ...configArray ] }, this.untransformOrg.bind(this, org, false));
                }}
            />
        </Paper>);

        configTabs.push(<Paper key='Roles'>
            <RolesView appContext={appContext} org={org} />
        </Paper>);

        configTabs.push(<Paper key='Org Fields'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Additional fields that will be gathered when registering a new patient.</Typography>
            <EnhancedTable
                title='Org Fields'
                rowsPerPage={5}
                orderBy='key'
                columnData={orgFieldsColumns}
                data={org.orgFields}
                onDataChanged={(data, table) => {
                    org.orgFields = data;
                    this.setState({ org });
                }}
            />
        </Paper>);

        configTabs.push(<Paper key='Text Overrides'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Organization text overrrides of any text strings in the patient web client or back-end.</Typography>
            <TextOverrides
                appContext={appContext}
                org={org}
                onChange={(org) => this.setState({ org }, this.transformOrg.bind(this,org) )}
            />
        </Paper>);

        configTabs.push(<Paper key='Appointment Templates'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Additional appointment message types that will appear when creating an appointment in the provider web client.</Typography>
            <AppointmentTemplates
                appContext={appContext}
                org={org}
                onChange={(org) => this.setState({ org }, this.transformOrg.bind(this,org) )}
            />
        </Paper>);

        configTabs.push(<Paper key='Onboarding Completion'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Numbered guidance steps shown on the final "Completion" step of the provider portal onboarding wizard. Each step has a title, optional detail sub-line, an optional link button that opens a URL in a new tab, and an optional inline warning box.</Typography>
            <CompletionConfig
                appContext={appContext}
                org={org}
                onChange={(org) => this.setState({ org }, this.transformOrg.bind(this,org) )}
            />
        </Paper>);

        configTabs.push(<Paper key='Pre-Visit Actions'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Pre-visit action buttons on the patient dashboard, shown under a "Before your visit" header. Each button gates on getPatientData (Data ID within N days) and, when clicked, opens a check-in restricted to the listed models. When all buttons are gated, an "all caught up" message is shown instead.</Typography>
            <PreVisitActionsConfig
                appContext={appContext}
                org={org}
                onChange={(org) => this.setState({ org }, this.transformOrg.bind(this,org) )}
            />
        </Paper>);

        configTabs.push(<Paper key='Additional Terms'>
            <Typography style={{ margin: 5, fontStyle: 'italic' }}>Additional terms that will be required of any patients with this as their primary organization.</Typography>
            <AdditionalTerms
                appContext={appContext}
                org={org}
                onChange={(org) => this.setState({ org }, this.transformOrg.bind(this,org) )}
            />
        </Paper>);// ONLY `enabled` Organizational-Rosters display Roster Tab

        if (org.enableRoster) {
             tabs.push(<Paper key='Roster'>
                <RosterView appContext={appContext} org={org} />
            </Paper>);
        }
        tabs.push(<Paper key='Registration Codes'>
            <CodeView appContext={appContext} org={org} />
        </Paper>);

        const displayConfigTabs = configTabs.map((t,i) => <Tab key={i} label={t.key} value={`${i}`} />)
        tabs.push(<div key='Config'>
            {displayConfigTabs.length > 0 && [
                <Tabs key='tab_bar' variant='scrollable' style={{ margin: 0 }}
                    value={currentConfigTab} onChange={(e,t) => { this.setState({ currentConfigTab: `${t}` }, this.createTabs.bind(this)); }}
                    children={displayConfigTabs} />,
                configTabs[currentConfigTab],
                <br key='br'/>
            ]}
        </div>);

        this.setState({ tabs, configTabs });
    }


    render() {
        const {
            props: { appContext },
            state: { dialog, error, export_progress, org, progress, tabs }
        } = this,
            currentTab = (this.state.currentTab < 0 || this.state.currentTab >= tabs.length) ? '0' : this.state.currentTab;

        const { userId } = appContext.state;
        const appBar = (typeof this.props.onClose === 'function') ?
            <AppBar position='static' className='appbar'><Toolbar>
                <IconButton onClick={this.closeOrg.bind(this)}><NavigationClose /></IconButton>
                <Typography color='inherit'>Organization Details</Typography>
            </Toolbar></AppBar>
        : null;
        let editOrg = null;


        if (org) {
            if (org.shortName === undefined) org.shortName = '';
            if (org.billingType === undefined) org.billingType = 'free';
            if (org.square === undefined) org.square = { accessToken: '', locationId: '', signatureKey: '' };
            if (org.square.accessToken === undefined) org.square.accessToken = '';
            if (org.square.locationId === undefined) org.square.locationId = '';
            if (org.square.signatureKey === undefined) org.square.signatureKey = '';
            if (!Array.isArray(org.NPIs)) org.NPIs = [];

            const displayTabs = tabs.map((t,i) => <Tab key={i} label={t.key} value={`${i}`} />),
                importButton = <ImportExport asText={true} collectQuantity={true} ignoreImport={true}
                    toolTip='Activation Codes'
                    name='Activation Codes'
                    note='Specify the number of activation codes to export'
                    exportStart={(limit) => {
                        return this.exportActivationCodes(limit)
                    }}
                    exportRow={() => Promise.resolve()}
                    importStart={() => Promise.resolve()}
                    importRows={(rows) => this.importRoster(rows)}
                    importRow={() => Promise.resolve()}
                    importDone={() => Promise.resolve()}
                />;

            editOrg = <div className='drawer'>Org ID: {org.orgId}
                <span className='error'>{error}</span>
                <span className='icons'>
                    <IconButton disabled={progress != null} onClick={this.saveOrg.bind(this)}>
                        {progress ? progress : <SaveIcon />}
                    </IconButton>
                    <IconButton onClick={this.onShowCode.bind(this)}><CodeIcon /></IconButton>
                    <IconButton onClick={this.onDeleteOrg.bind(this)}><DeleteIcon /></IconButton>
                    <Tooltip title='Merge Org'>
                        <IconButton onClick={this.onMergeOrg.bind(this)}><MergeIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title='Export Patients'>
                        <IconButton onClick={this.exportPatients.bind(this)}><ExportIcon /></IconButton>
                    </Tooltip>
                    <Tooltip title='Import Patients'>
                        <IconButton onClick={this.confirmImportPatients.bind(this)}><ImportIcon /></IconButton>
                    </Tooltip>
                </span>
                <div>
                    <TextField required component='form' name='orgName' label='Name' style={{ margin: 5, width: 400 }}
                        value={org.name} onChange={(e) => { org.name = e.target.value; this.setState({ org }); }} />
                    <TextField required component='form' name='orgShortName' label='Short Name' style={{ margin: 5, width: 150 }}
                        value={org.shortName} onChange={(e) => { org.shortName = e.target.value; this.setState({ org }); }} />
                    <TextField required select multiline component='form' name='orgType' label='Type' placeholder='Operational Type' style={{margin: 5, width: 200}}
                        value={org.orgType || 'unknown'} onChange={this.onOrgType.bind(this)}
                            children={ORG_TYPES} />
                    <SelectTimezone timezone={org.timeZone} onChange={this.onTimeZone.bind(this)} />
                    {/* <TextField required select component='form' name='timezone' label='Timezone' style={{margin: 5, width: 200}}
                        value={org.timeZone || 'UTZ'} onChange={this.onTimeZone.bind(this)}
                            children={TIMEZONES} /> */}
                    <TextField component='form' name='customURL' label='Logo URL' style={{ margin: 5, width: 400 }}
                        value={org.customLogo} onChange={(e) => void (
                            org.customLogo = String(e.target.value || ''),
                            this.setState({ org })
                        )}
                    />
                    <Tooltip title='View Image'><IconButton onClick={() => {
                        this.setState({// DISPLAY Popup Dialog<Image>
                            dialog: <Dialog open maxWidth='lg' onClose={this.onCloseDialog.bind(this)}>
                                <DialogContent><div align='center'>
                                    <img src={org.customLogo} alt='logo' width='100%' />
                                </div></DialogContent>
                                <DialogActions>
                                    <Button onClick={this.onCloseDialog.bind(this)}>Close</Button>
                                </DialogActions>
                            </Dialog>
                        });
                    }}><ViewIcon /></IconButton></Tooltip>
                    <UploadImage folderPath='logos/' appContext={appContext} onChange={(e) => {
                        org.customLogo = e; this.setState({ org }) }} />
                    <br />
                    <TextField component='form' name='idPrefix' label='ID Prefix' value={org.idPrefix} style={{ margin: 5, width: '10%' }}
                        onChange={(e) => void (
                            org.idPrefix = String(e.target.value || '').toUpperCase(),
                            this.setState({ org })
                        )}
                    />
                    <TextField component='form' name='patientDNS' label='Patient DNS' style={{ margin: 5, width: '20%' }}
                        value={org.patientDNS} onChange={(e) => void (
                            org.patientDNS = String(e.target.value || '').toLowerCase(),
                            this.setState({ org })
                        )}
                    />
                    <TextField component='form' name='providerDNS' label='Provider DNS' style={{ margin: 5, width: '20%' }}
                        value={org.providerDNS} onChange={(e) => void (
                            org.providerDNS = String(e.target.value || '').toLowerCase(),
                            this.setState({ org })
                        )}
                    />
                    <TextField component='form' name='directMessageDomain' label='DirectEMR Domain' style={{ margin: 5, width: '30%' }}
                        value={org.directMessageDomain} onChange={(e) => void (
                            org.directMessageDomain = String(e.target.value || '').toLowerCase(),
                            this.setState({ org })
                        )}
                    />
                    <br />
                    <FormControl style={{margin: 5, width: 250}}>
                        <InputLabel>Billing Type</InputLabel>
                        <Select value={org.billingType} onChange={(e) => {
                            org.billingType = e.target.value;
                            this.setState({org});
                        }}>
                            <MenuItem value='free'>Free</MenuItem>
                            <MenuItem value='invoiced'>Invoiced</MenuItem>
                            <MenuItem value='square'>Square</MenuItem>
                        </Select>
                    </FormControl>
                    {org.billingType === 'square' ? 
                        <SelectSubscriptionPlans appContext={appContext} orgId={org.orgId} value={org.subscriptionPlanIds} onChange={(e) => {
                            console.log("onChange subscription plans:", e );
                            org.subscriptionPlanIds = e;
                            this.setState({ org })
                        }} /> : null
                    }
                    {org.billingType === 'square' ?
                        <TextField label='Square Token' style={{ margin: 5, width: 300 }} value={org.square.accessToken} onChange={(e) => {
                            org.square.accessToken = e.target.value;
                            this.setState({ org })
                        }} /> : null
                    }
                    {org.billingType === 'square' ?
                        <TextField label='Square Location ID' style={{ margin: 5, width: 300 }} value={org.square.locationId} onChange={(e) => {
                            org.square.locationId = e.target.value;
                            this.setState({ org })
                        }} /> : null
                    }
                    {org.billingType === 'square' ?
                        <TextField label='Square Webhook Signature' style={{ margin: 5, width: 300 }} value={org.square.signatureKey} onChange={(e) => {
                            org.square.signatureKey = e.target.value;
                            this.setState({ org })
                        }} /> : null
                    }
                    <br />
                    <div style={styles.divRow}>
                        <SelectProvider enableNone label='Default Provider' style={{margin: 5, width: 300}}
                            appContext={appContext} providerId={org.defaultProviderId} orgId={org.orgId}
                            onSelect={(id) => { org.defaultProviderId = id; this.setState({ org })} }
                        />
                        <SelectProvider enableNone label='Register Notify Provider' style={{margin: 5, width: 300}}
                            appContext={appContext} providerId={org.registerProviderId} orgId={org.orgId}
                            onSelect={(id) => { org.registerProviderId = id; this.setState({ org })} }
                        />
                        <Tooltip title='Organization NPIs comma seperated'><TextField label="NPIs" style={{margin: 5, width: 300}} value={org.NPIs.join(',')} onChange={(e) => {
                            org.NPIs = e.target.value.split(',');
                            this.setState({org})
                        }} /></Tooltip>
                        <SelectLanguage enableNone label='Default Language' style={{margin: 5, width: 300}}
                            language={org.defaultLanguage} onChange={(lang) => { org.defaultLanguage = lang; this.setState({org}) }}
                        />
                    </div>
                    <br />
                    <FormControlLabel label='Inactive' control={
                        <Checkbox checked={org.inactive} onChange={(e,v) => { 
                            org.inactive = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Appointments' control={
                        <Checkbox checked={org.enableAppointments} onChange={(e,v) => {
                            org.enableAppointments = v; this.setState({ org }, this.createTabs.bind(this)) }} />
                    } />
                    <FormControlLabel label='Enable Roster' control={
                        <Checkbox checked={org.enableRoster} onChange={(e,v) => {
                            org.enableRoster = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Activation Code' control={
                        <Checkbox checked={org.enableActivationCode} onChange={(e,v) => {
                            org.enableActivationCode = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Registration Code' control={
                        <Checkbox checked={org.enableRegistrationCode} onChange={(e,v) => {
                            org.enableRegistrationCode = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Registration Notification' control={
                        <Checkbox checked={org.enableRegisterNotification} onChange={(e,v) => {
                            org.enableRegisterNotification = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Triaged Notification' control={
                        <Checkbox checked={org.enableTriagedNotification} onChange={(e,v) => {
                            org.enableTriagedNotification = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Require Registration Codes' control={
                        <Checkbox checked={org.requireRegistrationCode} onChange={(e,v) => {
                            org.requireRegistrationCode = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Enable Provider Search' control={
                        <Checkbox checked={org.enableProviderSearch} onChange={(e,v) => {
                            org.enableProviderSearch = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Disable EMR' control={
                        <Checkbox checked={org.disableEMR} onChange={(e,v) => {
                            org.disableEMR = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Require MFA' control={
                        <Checkbox checked={org.requireMFA} onChange={(e,v) => {
                            org.requireMFA = v; this.setState({ org }) }} />
                    } />
                    <FormControlLabel label='Disable Reporting' control={
                        <Checkbox checked={org.disableReporting} onChange={(e,v) => {
                            org.disableReporting = v; this.setState({ org }) }} />
                    } />
                    <br />
                    <FormControlLabel control={<Checkbox checked={isValidDate(org.validationDate)} onChange={(e, v) => {
                        org.validationDate = v ? new Date().toISOString() : null;
                        org.validatedBy = userId;
                        this.setState({ org });
                    }} />} label={org.validationDate ? `Validated on ${org.validationDate}` : "Validate"} />
                    {org.validationDate ? <span><i> validated by <User appContext={appContext} userId={org.validatedBy} userIdHidden={true} /></i></span> : null}
                    {org.enableActivationCode === true && importButton}
                    <br />
                    <FormControlLabel label='Enable Overnight Calls' control={
                        <Checkbox checked={org.enableOvernightCalls} onChange={(e,v) => {
                            org.enableOvernightCalls = v; this.setState({ org }) }} />
                    } />
                    {org.enableOvernightCalls && getTimePicker("Start Time", org.overnightHours.start, org.timeZone, (start) => {
                        org.overnightHours.start = start;
                        this.setState({ org })
                    })}
                    {org.enableOvernightCalls && getTimePicker("End Time", org.overnightHours.end, org.timeZone, (end) => {
                        org.overnightHours.end = end;
                        this.setState({ org })
                    })}
                </div>
                {displayTabs.length > 0 && [
                    <Tabs key='tab_bar' className='contact_table' variant='scrollable'
                        value={currentTab} onChange={(e,t) => { this.setState({ currentTab: `${t}` }); }}
                        children={displayTabs} />,
                    tabs[currentTab],
                    <br key='br'/>
                ]}
                <OwnerInfo appContext={appContext} model={org} />
            </div>;
        }


        return <div className='view'>
            {appBar}
            {export_progress}
            {editOrg}
            {dialog}
        </div>;
    }
};


/**
 * @extends React.Component
 * Organization Patient-Data Importer
 * @memberof module:Admin
 * @alias PatientsImporter
 */
class PatientsImport extends React.Component {
    constructor (props) {
        super(props);
        this.state = {
            bucket: null,
            firstName: null,
            lastName: null,
            newPatientId: false,
            providerId: null,
            userId: null
        };

        if (typeof this.props.cancel !== 'function')
            log.warning('`PatientsImport.onCancel()` event not found');
    }


    ok() {
        if (typeof this.props.ok === 'function') {
            const { state: { bucket, firstName, lastName, newPatientId, providerId, userId } } = this;
            this.props.ok({ bucket, firstName, lastName, newPatientId, providerId, userId });
        }
    }

    cancel() {
        if (typeof this.props.cancel === 'function') this.props.cancel();
    }


    render() {
        const {
            props: { appContext },
            state: { bucket, firstName, lastName, newPatientId, providerId, userId }
        } = this;

        return <Dialog open maxWidth='lg' aria-labelledby="customized-dialog-title">
            <DialogTitle id='customized-dialog-title'>Organization's patients import</DialogTitle>
            <DialogContent style={{ height: '400px' }}>
                <Typography variant='title' style={{ color: 'blue' }}>Please take care importing patients into the same env. Patient IDs are preserved and it will modify the existing patient.</Typography>
                <br />
                <Typography>Patients exported from an org will be imported into this org. If the org was a roster org the roster will be imported accordingly.</Typography>
                <table><tbody>
                    <tr>
                        <td align='left'>
                            <TextField label='First Name' value={firstName} style={styles.text}
                                onChange={(e) => { this.setState({ firstName: e.target.value }); }}
                            />
                            <TextField label='Last Name' value={lastName} style={styles.text}
                                onChange={(e) => { this.setState({ lastName: e.target.value }); }}
                            />
                            <Typography component='span' variant='caption'>Note: If you leave the first and last name empty it will use the preset naming of: First -Exported Last - [patientId].  If you provide a last name it will incremented: LastName1, LastName2, etc.</Typography>
                        </td>
                    </tr>
                    <tr>
                        <td align='left'>
                            <TextField label='File id from the export:' style={styles.text} value={bucket}
                                onChange={(e) => { this.setState({ bucket: e.target.value }); }}
                            />
                            <FormControlLabel label='Generate Patient Id' control={
                                <Checkbox name='Generate Patient Id' checked={newPatientId} style={{ margin: '5px' }}
                                    onChange={(e,v) => { this.setState({ newPatientId: v }); }}
                                />} />
                            <Typography component='span' variant='caption'>Note:  If you check the box 'Generate Patient Id' then it will NOT use the existing patientId but generate one randomly.</Typography>

                        </td>
                    </tr>
                    <tr>
                        <td align='left'>
                            <SelectProvider label='Provider' value={providerId} style={styles.select}
                                appContext={appContext} onChange={this.setState} />
                            <SelectUser label='User Account' groups='patients' userId={userId}
                                style={styles.select} appContext={appContext} onChange={this.setState} />
                            <Typography component='span' variant='caption'>Note:  If you provide a 'user' account this will allow you to use the specified account to login to ALL of the exported patients for this org with that account. When you login it will give you a selection to scroll through the list.  If you provide a 'Provider' account it will set that provider as the primary care provider for ALL the exported patients.</Typography>
                        </td>
                    </tr>
                </tbody></table>
            </DialogContent>
            <DialogActions>
                <Button variant='contained' onClick={this.cancel.bind(this)}>Cancel</Button>,
                <Button variant='contained' onClick={this.ok.bind(this)}>Confirm</Button>
            </DialogActions>
        </Dialog>;
    }
};


const styles = {
    idPrefix: { width: 100 },
    select: { margin: 5, overflow: 'visible', width: 400 },
    text: { margin: 5, width: 400 },
    divRow: {
        display: 'flex',
        flexWrap: 'wrap',
        flexDirection: 'row'
    }    
};


export default OrgView;